import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';

const StatCard = ({ icon, number, label, index }) => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  
  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        delay: 0.1 * index,
        ease: "easeOut"
      }
    }
  };
  
  const numberVariants = {
    hidden: { opacity: 0, scale: 0.5 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        delay: 0.2 + (0.1 * index),
        type: "spring",
        stiffness: 200
      }
    }
  };
  
  const iconVariants = {
    hidden: { opacity: 0, rotateY: 90 },
    visible: {
      opacity: 1,
      rotateY: 0,
      transition: {
        duration: 0.6,
        delay: 0.1 * index,
        type: "spring",
        stiffness: 200
      }
    }
  };
  
  return (
    <motion.div
      className="stat-card"
      ref={ref}
      variants={cardVariants}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
    >
      <motion.div
        className="stat-icon"
        variants={iconVariants}
      >
        {icon}
      </motion.div>
      <motion.div
        className="stat-number"
        variants={numberVariants}
      >
        {number}
      </motion.div>
      <div className="stat-label">{label}</div>
    </motion.div>
  );
};

const Stats = () => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };
  
  const headerVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };
  
  const buttonVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        delay: 0.4,
        duration: 0.4,
        type: "spring",
        stiffness: 200
      }
    },
    hover: {
      scale: 1.05,
      boxShadow: "0px 6px 15px rgba(0, 92, 83, 0.3)",
      transition: {
        duration: 0.2,
        type: "spring",
        stiffness: 400
      }
    },
    tap: { scale: 0.95 }
  };
  
  return (
    <section className="stats-section" ref={ref}>
      <div className="stats-container">
        <motion.div
          className="stats-header"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <motion.h2
            className="stats-title"
            variants={headerVariants}
          >
            We achieve scalable growth with ease
          </motion.h2>
          <motion.button
            className="scale-button"
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
          >
            Let's scale together
          </motion.button>
        </motion.div>
        
        <div className="stats-grid">
          <StatCard
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
            }
            number="151,000+"
            label="app downloads"
            index={0}
          />
          
          <StatCard
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="18" cy="5" r="3"></circle>
                <circle cx="6" cy="12" r="3"></circle>
                <circle cx="18" cy="19" r="3"></circle>
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
              </svg>
            }
            number="3,000+"
            label="drive partners"
            index={1}
          />
          
          <StatCard
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                <polyline points="9 22 9 12 15 12 15 22"></polyline>
              </svg>
            }
            number="1,500+"
            label="super merchants"
            index={2}
          />
          
          <StatCard
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="20" x2="12" y2="10"></line>
                <line x1="18" y1="20" x2="18" y2="4"></line>
                <line x1="6" y1="20" x2="6" y2="16"></line>
              </svg>
            }
            number="2,448x"
            label="growth in transactions the last 8 months"
            index={3}
          />
        </div>
      </div>
    </section>
  );
};

export default Stats;